import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, IconButton, TextField, Button, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import DoorBackIcon from '@mui/icons-material/DoorBack';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import axios from 'axios';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@mui/material';

const ConfigCards = ({ onAnnotateImage, onServiceSelected, annotatedZones = {}, zoneColors = {} }) => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [editingZone, setEditingZone] = useState(null);
  const [editingZoneValue, setEditingZoneValue] = useState('');
  const [customZones, setCustomZones] = useState({});
  const [additionalZones, setAdditionalZones] = useState({});
  const [newZoneName, setNewZoneName] = useState('');
  const [isAddingZone, setIsAddingZone] = useState(false);
  const [processedCardConfig, setProcessedCardConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const source = JSON.parse(localStorage.getItem('source'));

  // Define fixed zones for each service
  const serviceFixedZones = {
    'Queue Service': ['queue', 'cashier'],
    'Entry Exit Service': ['inside', 'outside']
  };

  const getIconForService = (serviceName) => {
    const iconStyle = {
      background: 'linear-gradient(135deg, #44BE54, #2AA93E)',
      color: 'white',
      borderRadius: '1.5rem',
      padding: '10px',
      boxShadow: '0px 6px 20px rgba(68, 190, 84, 0.35)',
      transition: 'all 0.3s ease-in-out',
      '&:hover': {
        background: 'linear-gradient(135deg, #3AA74A, #259435)',
        transform: 'translateY(-2px)',
        boxShadow: '0px 8px 25px rgba(68, 190, 84, 0.55)'
      }
    };

    switch (serviceName) {
      case 'Entry Exit Service':
        return <DoorBackIcon fontSize="large" sx={iconStyle} />;
      case 'Department Service':
        return <ApartmentIcon fontSize="large" sx={iconStyle} />;
      case 'Queue Service':
        return <PointOfSaleIcon fontSize="large" sx={iconStyle} />;
      case 'Pathway Service':
        return <AltRouteIcon fontSize="large" sx={iconStyle} />;
      default:
        return <DoorBackIcon fontSize="large" sx={iconStyle} />;
    }
  };

  const fetchServices = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post('http://localhost:7071/api/sentinel/defaultServices', {
        source_id: source
      });

      const data = response.data;

      if (data && data.services && Array.isArray(data.services)) {
        const transformedConfig = data.services.map((service) => ({
          _id: service._id,
          name: service.name,
          description: service.descriptions || '',
          pipeline_path: service.pipeline_path || '',
          zones: service.zones || [],
          fixedZones: service.fixed_zones || false
        }));

        setProcessedCardConfig(transformedConfig);
      } else {
        throw new Error('Invalid data format received from API');
      }
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(`Failed to load services: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getZoneColor = (cardName, zone) => {
    const zoneName = `${cardName}_${getZoneDisplayName(zone)}`.replace(/\s+/g, '_');
    return zoneColors[zoneName] || null;
  };

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (processedCardConfig.length > 0) {
      const updatedConfig = processedCardConfig.map((card) => {
        let zones = [];

        if (card.fixedZones) {
          // For fixed zones services, use the predefined zones
          zones = serviceFixedZones[card.name] || card.zones || [];
        } else {
          // For non-fixed zones, use additional zones added by the user
          zones = additionalZones[card.name] || [];
        }

        return { ...card, zones };
      });

      setProcessedCardConfig(updatedConfig);

      if (selectedCard) {
        const updatedSelectedCard = updatedConfig.find((card) => card.name === selectedCard.name);
        if (updatedSelectedCard) {
          setSelectedCard(updatedSelectedCard);
        }
      }
    }
  }, [additionalZones, selectedCard?.name]);

  const handleCardClick = (card) => {
    setSelectedCard(card);
    setEditingZone(null);
    setIsAddingZone(false);
    setNewZoneName('');

    if (onServiceSelected) {
      onServiceSelected(card.name, card._id);
    }
  };

  const handleZoneAnnotate = (zone) => {
    if (isZoneAnnotated(selectedCard.name, zone)) return;

    const zoneDisplayName = getZoneDisplayName(zone);

    if (onAnnotateImage) {
      onAnnotateImage({
        card: selectedCard.name,
        zone: zoneDisplayName
      });
    }
  };

  const handleEditZone = (zone) => {
    if (isZoneAnnotated(selectedCard.name, zone)) return;
    if (selectedCard.fixedZones) return;

    setEditingZone(zone);
    setEditingZoneValue(customZones[selectedCard.name]?.[zone] || zone);
    setIsAddingZone(false);
  };

  const handleSaveZoneName = () => {
    if (editingZoneValue.trim() === '') return;

    setCustomZones((prev) => ({
      ...prev,
      [selectedCard.name]: {
        ...(prev[selectedCard.name] || {}),
        [editingZone]: editingZoneValue
      }
    }));

    setEditingZone(null);
  };

  const getAddButtonText = (serviceName) => {
    switch (serviceName) {
      case 'Department Service':
        return 'Add Department';
      case 'Pathway Service':
        return 'Add Pathway';
      default:
        return 'Add Zone';
    }
  };

  const getZoneTypeForService = (serviceName) => {
    switch (serviceName) {
      case 'Entry Exit Service':
        return 'Entry/Exit';
      case 'Department Service':
        return 'Department';
      case 'Queue Service':
        return 'Queue';
      default:
        return 'Zone';
    }
  };

  const handleAddZoneClick = () => {
    // Only allow adding zones for non-fixed zone services
    if (selectedCard.fixedZones) {
      return;
    }

    setIsAddingZone(true);
    setEditingZone(null);
    setNewZoneName('');
  };

  const handleSaveNewZone = () => {
    if (newZoneName.trim() === '') return;

    const cardName = selectedCard.name;
    const newZoneId = `${cardName}_${Date.now().toString().slice(-4)}`;

    setAdditionalZones((prev) => ({
      ...prev,
      [cardName]: [...(prev[cardName] || []), newZoneId]
    }));

    setCustomZones((prev) => ({
      ...prev,
      [cardName]: {
        ...(prev[cardName] || {}),
        [newZoneId]: newZoneName
      }
    }));

    setNewZoneName('');
    setIsAddingZone(false);
  };

  const getZoneDisplayName = (zone) => {
    // For fixed zones, return the zone name as-is
    if (selectedCard?.fixedZones && serviceFixedZones[selectedCard.name]?.includes(zone)) {
      return zone;
    }
    // For custom zones, return the custom name
    return customZones[selectedCard?.name]?.[zone] || zone;
  };

  const isZoneAnnotated = (cardName, zone) => {
    const displayName = getZoneDisplayName(zone);
    const formattedZoneName = `${cardName}_${displayName.replace(/\s+/g, '_')}`;

    return (
      annotatedZones[cardName]?.some((name) => {
        const baseName = name.includes('_') ? name.split('_').slice(1).join('_') : name;
        const zoneName = displayName.replace(/\s+/g, '_');

        return name === formattedZoneName || baseName === zoneName;
      }) || false
    );
  };

  const handleBack = () => {
    setSelectedCard(null);
    if (onServiceSelected) {
      onServiceSelected('Services');
    }
  };

  // Check if the service allows adding zones
  const canAddZones = (serviceName) => {
    // Only allow adding zones for non-fixed zone services
    const card = processedCardConfig.find((c) => c.name === serviceName);
    return !card?.fixedZones && (serviceName === 'Pathway Service' || serviceName === 'Department Service');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress sx={{ color: '#44BE54' }} />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading services...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          p: 4,
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          bgcolor: 'rgba(0, 0, 0, 0.02)',
          textAlign: 'center'
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          No Services Found
        </Typography>
        <Typography variant="body1" color="text.secondary">
          No services found for {source || 'this store'}
        </Typography>
        <Button variant="outlined" color="primary" onClick={fetchServices} sx={{ mt: 3 }}>
          Refresh
        </Button>
      </Box>
    );
  }

  return (
    <Box className="config-cards-container" sx={{ width: '100%' }}>
      {!selectedCard ? (
        <>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: '#333',
              position: 'relative',
              display: 'inline-block',
              pb: 1,
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '60%',
                height: '3px',
                background: 'linear-gradient(90deg, #44BE54, transparent)',
                borderRadius: '4px'
              }
            }}
          >
            Choose a Service
          </Typography>
          <Box
            sx={{
              height: '500px',
              width: '100%',
              border: '1px solid rgba(0,0,0,0.08)',
              borderRadius: 2,
              backgroundColor: '#fafafa',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <Box
              display="grid"
              gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(2, 1fr)' }}
              gap={3}
              sx={{
                width: '100%',
                flex: 1,
                overflowY: 'auto',
                padding: 3,
                paddingRight: 2,
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(68, 190, 84, 0.6) rgba(0,0,0,0.05)',
                '&::-webkit-scrollbar': {
                  width: '8px'
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(0,0,0,0.05)',
                  borderRadius: '4px'
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(68, 190, 84, 0.6)',
                  borderRadius: '4px',
                  '&:hover': {
                    background: 'rgba(68, 190, 84, 0.8)'
                  }
                },
                scrollbarGutter: 'stable'
              }}
            >
              {processedCardConfig.map((card, index) => (
                <Card
                  key={index}
                  onClick={() => handleCardClick(card)}
                  sx={{
                    height: '100%',
                    minHeight: 160,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    border: '1px solid rgba(0,0,0,0.04)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 16px 40px rgba(68, 190, 84, 0.12)'
                    },
                    background: 'linear-gradient(145deg, #ffffff, #f8f9fa)',
                    position: 'relative',
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      background: 'linear-gradient(90deg, #44BE54, #2AA93E)',
                      opacity: 0.8
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 2,
                    textAlign: 'center'
                  }}
                >
                  <Box sx={{ fontSize: 48, color: '#44BE54', mb: 1 }}>{getIconForService(card.name)}</Box>

                  <Typography variant="h4" sx={{ fontWeight: 500, color: '#333', marginTop: '10px' }}>
                    {card.name}
                  </Typography>

                  <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <Tooltip title={card.description} arrow placement="top">
                      <IconButton
                        size="small"
                        sx={{
                          color: '#999',
                          '&:hover': {
                            color: '#44BE54',
                            backgroundColor: 'rgba(68, 190, 84, 0.1)'
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Card>
              ))}
            </Box>
          </Box>
        </>
      ) : (
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 4,
              pb: 2,
              borderBottom: '1px solid rgba(0,0,0,0.08)'
            }}
          >
            <IconButton
              onClick={handleBack}
              sx={{
                mr: 2,
                bgcolor: 'rgba(0,0,0,0.04)',
                '&:hover': {
                  bgcolor: 'rgba(68, 190, 84, 0.1)',
                  transform: 'translateX(-3px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <ArrowBackIcon />
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ mr: 2 }}>{getIconForService(selectedCard.name)}</Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  backgroundImage: 'linear-gradient(45deg, #333, #555)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {selectedCard.name}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 500,
                  position: 'relative',
                  display: 'inline-block',
                  pb: 1,
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '40%',
                    height: '2px',
                    background: 'linear-gradient(90deg, #44BE54, transparent)',
                    borderRadius: '4px'
                  }
                }}
              >
                {selectedCard.fixedZones ? 'Required' : 'Available'} {getZoneTypeForService(selectedCard.name)}s
              </Typography>

              {canAddZones(selectedCard.name) && (
                <Button
                  onClick={handleAddZoneClick}
                  startIcon={<AddCircleIcon />}
                  variant="contained"
                  sx={{
                    bgcolor: '#44BE54',
                    '&:hover': {
                      bgcolor: '#3AAD4A',
                      transform: 'translateY(-2px)',
                      boxShadow: '0px 6px 16px rgba(68, 190, 84, 0.35)'
                    },
                    borderRadius: 2,
                    px: 3,
                    py: 1.2,
                    boxShadow: '0px 4px 14px rgba(68, 190, 84, 0.25)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {getAddButtonText(selectedCard.name)}
                </Button>
              )}
            </Box>

            {selectedCard.fixedZones && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(68, 190, 84, 0.1)', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Note:</strong> This service has predefined zones that cannot be modified. You can only annotate the required
                  zones: {serviceFixedZones[selectedCard.name]?.join(', ')}.
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)'
                },
                gap: 3
              }}
            >
              {isAddingZone && canAddZones(selectedCard.name) && (
                <Card
                  sx={{
                    bgcolor: 'rgba(68, 190, 84, 0.05)',
                    border: '1px dashed #44BE54',
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0px 4px 16px rgba(68, 190, 84, 0.1)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    <TextField
                      fullWidth
                      autoFocus
                      placeholder={`Enter new ${getZoneTypeForService(selectedCard.name).toLowerCase()} name`}
                      value={newZoneName}
                      onChange={(e) => setNewZoneName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSaveNewZone()}
                      variant="outlined"
                      size="small"
                      sx={{
                        mr: 1,
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'rgba(68, 190, 84, 0.4)'
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(68, 190, 84, 0.7)'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#44BE54'
                          }
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleSaveNewZone}
                      sx={{
                        color: '#44BE54',
                        '&:hover': {
                          bgcolor: 'rgba(68, 190, 84, 0.15)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <CheckIcon />
                    </IconButton>
                  </CardContent>
                </Card>
              )}

              {selectedCard?.zones?.length > 0 ? (
                selectedCard.zones.map((zone, index) => {
                  const isAnnotated = isZoneAnnotated(selectedCard.name, zone);
                  const isFixedZone = selectedCard.fixedZones;

                  return (
                    <Card
                      key={index}
                      onClick={() => !isAnnotated && handleZoneAnnotate(zone)}
                      sx={{
                        borderRadius: 2,
                        cursor: isAnnotated ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        bgcolor: isAnnotated ? 'rgba(0, 0, 0, 0.04)' : 'white',
                        border: isAnnotated
                          ? '1px solid rgba(0, 0, 0, 0.12)'
                          : `1px solid ${getZoneColor(selectedCard.name, zone) || (isFixedZone ? '#44BE54' : 'rgba(0, 0, 0, 0.08)')}`,
                        opacity: isAnnotated ? 0.7 : 1,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                          boxShadow: isAnnotated ? 'none' : '0 6px 18px rgba(68, 190, 84, 0.25)',
                          transform: isAnnotated ? 'none' : 'translateY(-5px)',
                          bgcolor: isAnnotated ? 'rgba(0, 0, 0, 0.04)' : 'rgba(68, 190, 84, 0.05)',
                          borderColor: isAnnotated ? 'rgba(0, 0, 0, 0.12)' : `${getZoneColor(selectedCard.name, zone) || '#44BE54'}`
                        },
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        {editingZone === zone && !isFixedZone ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TextField
                              fullWidth
                              autoFocus
                              value={editingZoneValue}
                              onChange={(e) => setEditingZoneValue(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleSaveZoneName()}
                              onClick={(e) => e.stopPropagation()}
                              variant="outlined"
                              size="small"
                              sx={{
                                mr: 1,
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: 'rgba(68, 190, 84, 0.4)'
                                  },
                                  '&:hover fieldset': {
                                    borderColor: 'rgba(68, 190, 84, 0.7)'
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#44BE54'
                                  }
                                }
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveZoneName();
                              }}
                              sx={{
                                color: '#44BE54',
                                '&:hover': {
                                  bgcolor: 'rgba(68, 190, 84, 0.15)',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <CheckIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 500,
                                  color: isAnnotated ? 'text.disabled' : 'text.primary',
                                  textDecoration: isAnnotated ? 'line-through' : 'none',
                                  letterSpacing: '0.2px',
                                  position: 'relative',
                                  '&:before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: -12,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: '6px',
                                    height: '6px',
                                    borderRadius: '50%',
                                    backgroundColor: getZoneColor(selectedCard.name, zone) || (isFixedZone ? '#44BE54' : 'transparent'),
                                    transition: 'background-color 0.3s ease'
                                  }
                                }}
                              >
                                {getZoneDisplayName(zone)}
                              </Typography>
                              {isAnnotated && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    ml: 1.5,
                                    bgcolor: 'rgba(0, 0, 0, 0.06)',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    color: 'text.secondary',
                                    fontWeight: 500
                                  }}
                                >
                                  Annotated
                                </Typography>
                              )}
                              {isFixedZone && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    ml: 1.5,
                                    bgcolor: 'rgba(68, 190, 84, 0.1)',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    color: '#44BE54',
                                    fontWeight: 500
                                  }}
                                ></Typography>
                              )}
                            </Box>

                            {!isAnnotated && !isFixedZone && editingZone !== zone && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditZone(zone);
                                }}
                                sx={{
                                  color: 'action.active',
                                  '&:hover': {
                                    color: '#44BE54',
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 3 }}>
                  No {getZoneTypeForService(selectedCard.name).toLowerCase()}s available for this service.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ConfigCards;
